import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { FormioForm } from 'src/components/FormioForm/FormioForm';
import { SpinnerFullWidth } from 'src/components/Spinner/SpinnerFullWidth';
import { Router } from 'src/core/router/Router';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { CombinedLoadingStatus } from 'src/utils/mobx/CombinedLoadingStatus';
import styled from 'styled-components';

export type VersionCreateProps = {
    store: BasicStore<any>;
};
export const VersionCreate = observer(({ store }: VersionCreateProps) => {
    const { params } = useRouteMatch<{ subEntityId: string }>();
    const { subEntityId } = params;
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        store.loadItem(subEntityId);
        store.loadForm(store.itemCreateFormKey);
        store.createItemLoader.reset();

        return () => {
            store.formLoader.reset();
        };
    }, [subEntityId, store]);

    const loadingStatus = useMemo(() => {
        return new CombinedLoadingStatus([store.formLoader]);
    }, [store.formLoader]);

    // CRITICAL: Use useCallback to create stable onSubmit reference with submission guard
    const handleSubmit = useCallback(
        async (submission) => {
            // Prevent multiple submissions
            if (isSubmitting) {
                console.warn(
                    'Submission already in progress, ignoring duplicate submission',
                );

                return;
            }

            setIsSubmitting(true);

            try {
                const newItem = await store.update?.(
                    subEntityId,
                    submission.data,
                );

                if (newItem) {
                    store.loadList();
                    const redirectUrl = `/settings/scoring-model/${newItem?.id}/versions/${newItem?.versionId}`;
                    Router.replace(redirectUrl);
                } else {
                    console.warn(
                        'newItem is null/undefined, redirect cancelled',
                    );
                }
            } catch (error) {
                console.error('Submission failed:', error);
            } finally {
                setIsSubmitting(false);
            }
        },
        [subEntityId, store, isSubmitting],
    ); // Only recreate when these values actually change

    if (loadingStatus.isLoading) {
        return <SpinnerFullWidth />;
    }

    return (
        <StyledWrapper>
            <FormioForm
                key={`create-${subEntityId}`}
                form={store.currentItemForm as any}
                submission={{ data: store.currentItem } as any}
                onSubmit={handleSubmit}
            />
        </StyledWrapper>
    );
});

const StyledWrapper = styled.span`
    .active-model {
        a {
            text-decoration: none;
            pointer-events: none;
        }
    }

    tr:has(.active-model) {
        background-color: #f0f0f0;
    }
`;
